import React, { useRef, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { FiUploadCloud, FiFile, FiX, FiCheckCircle } from 'react-icons/fi';

import { appImages } from '../../utils/constants';
import { shiprocketBulkUpload, uploadFiles } from '../../services/api';

// Human-readable labels for each bulk import tool
const TOOL_LABELS = {
  import_orders_bulk: {
    title: 'Bulk Import Orders',
    description:
      'Upload a CSV file to import multiple orders into Shiprocket at once.',
    accept: '.csv',
    hint: 'Accepts Shiprocket orders CSV format',
    mode: 'shiprocket',
  },
  bulk_import_products: {
    title: 'Bulk Import Products',
    description:
      'Upload a CSV file to import multiple products into your Shiprocket catalog.',
    accept: '.csv',
    hint: 'Accepts Shiprocket products CSV format',
    mode: 'shiprocket',
  },
  import_catalog_mappings: {
    title: 'Import Catalog Mappings',
    description: 'Upload a CSV file to map your catalog entries in Shiprocket.',
    accept: '.csv',
    hint: 'Accepts Shiprocket catalog mapping CSV format',
    mode: 'shiprocket',
  },
  jira_manage_issue: {
    title: 'Attach File to Jira Issue',
    description:
      'Upload a file to attach it to the Jira issue. Any file type is supported.',
    accept: '*',
    hint: 'The file will be uploaded and attached to the issue automatically.',
    mode: 'jira',
    icon: 'jira',
  },
};

/**
 * A single file upload card for one tool call that requires a file.
 */
const FileUploadCard = ({ toolCall, onComplete, onDismiss }) => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const funcName = toolCall.function?.name;
  const meta = TOOL_LABELS[funcName] || {
    title: funcName?.replace(/_/g, ' ') || 'Upload File',
    description: 'Upload a file to continue.',
    accept: '.csv',
    hint: 'CSV file required',
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) setSelectedFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) {
      toast.error('Please select a file first.');
      return;
    }
    setIsUploading(true);
    try {
      const accessToken = localStorage.getItem('projectToken');
      if (!accessToken)
        throw new Error('Not authenticated. Please log in again.');

      if (meta.mode === 'jira') {
        // --- Jira attach_file mode ---
        // 1. Upload the file to the standard /file/upload endpoint
        const taskId = toolCall._taskId || '';
        const uploadResult = await uploadFiles(accessToken, taskId, [
          selectedFile,
        ]);
        if (!uploadResult || uploadResult.error) {
          throw new Error(uploadResult?.error || 'File upload failed.');
        }
        const fileInfo = uploadResult?.files_info?.[0];
        if (!fileInfo?.s3_filename) {
          throw new Error('Upload succeeded but no file path was returned.');
        }
        // 2. Inject the s3_filename into the tool call payload so the backend
        //    can use it as the file_paths argument for jira_manage_issue attach_file
        const existingArgs =
          typeof toolCall.function?.arguments === 'string'
            ? JSON.parse(toolCall.function.arguments)
            : toolCall.function?.arguments || {};
        const updatedArgs = {
          ...existingArgs,
          payload: {
            ...(existingArgs.payload || {}),
            file_paths: [fileInfo.s3_filename],
          },
        };
        const updatedToolCall = {
          ...toolCall,
          function: {
            ...toolCall.function,
            arguments: JSON.stringify(updatedArgs),
          },
        };
        toast.success('File uploaded! Attaching to Jira issue…');
        onComplete(toolCall.id, { uploadResult, updatedToolCall });
      } else {
        // --- Shiprocket CSV mode (original) ---
        const result = await shiprocketBulkUpload(
          accessToken,
          funcName,
          selectedFile,
        );
        toast.success('File uploaded successfully!');
        onComplete(toolCall.id, result);
      }
    } catch (err) {
      toast.error(
        typeof err === 'string' ? err : err?.message || 'Upload failed.',
      );
    } finally {
      setIsUploading(false);
    }
  }, [selectedFile, funcName, meta.mode, toolCall, onComplete]);

  return (
    <div className="mb-4 w-full overflow-hidden rounded-2xl border border-zinc-700/60 bg-zinc-900 shadow-xl">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-zinc-700/50 bg-zinc-800/60 px-4 py-3">
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-zinc-700/80 p-1">
          {meta.icon === 'jira' ? (
            <img
              src={appImages.jira || '/assets/icons/jira_icon.png'}
              alt="Jira"
              className="h-full w-full object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <img
              src={appImages.shiprocket || '/assets/icons/shiprocket_logo.png'}
              alt="Shiprocket"
              className="h-full w-full object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-zinc-100">{meta.title}</p>
          <p className="truncate text-xs text-zinc-400">
            {meta.icon === 'jira' ? 'Jira' : 'Shiprocket'} · File upload
            required
          </p>
        </div>
        <button
          onClick={() => onDismiss(toolCall.id)}
          className="rounded-lg p-1.5 text-zinc-500 transition-colors hover:bg-zinc-700 hover:text-zinc-300"
          title="Dismiss"
        >
          <FiX className="h-4 w-4" />
        </button>
      </div>

      {/* Body */}
      <div className="p-4">
        <p className="mb-4 text-sm leading-relaxed text-zinc-400">
          {meta.description}
        </p>

        {/* Drop Zone — use <label> so it's natively interactive (fixes jsx-a11y) */}
        <input
          ref={fileInputRef}
          id={`file-upload-${toolCall.id}`}
          type="file"
          accept={meta.accept}
          onChange={handleFileSelect}
          className="hidden"
        />
        <label
          htmlFor={`file-upload-${toolCall.id}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`relative flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-8 transition-all duration-200 ${
            isDragOver
              ? 'border-blue-500 bg-blue-500/10'
              : selectedFile
                ? 'border-emerald-500/50 bg-emerald-500/5'
                : 'border-zinc-600 bg-zinc-800/40 hover:border-zinc-500 hover:bg-zinc-800/70'
          }`}
        >
          {selectedFile ? (
            <>
              <FiCheckCircle className="h-8 w-8 text-emerald-400" />
              <div className="text-center">
                <p className="text-sm font-medium text-emerald-300">
                  {selectedFile.name}
                </p>
                <p className="text-xs text-zinc-500">
                  {(selectedFile.size / 1024).toFixed(1)} KB · Click to change
                </p>
              </div>
            </>
          ) : (
            <>
              <FiUploadCloud
                className={`h-8 w-8 ${isDragOver ? 'text-blue-400' : 'text-zinc-500'}`}
              />
              <div className="text-center">
                <p className="text-sm font-medium text-zinc-300">
                  Drop your {meta.mode === 'jira' ? 'file' : 'CSV'} here, or{' '}
                  <span className="text-blue-400 underline underline-offset-2">
                    browse
                  </span>
                </p>
                <p className="mt-0.5 text-xs text-zinc-500">{meta.hint}</p>
              </div>
            </>
          )}
        </label>

        {/* File info pill */}
        {selectedFile && (
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-zinc-700/50 bg-zinc-800/50 px-3 py-2">
            <FiFile className="h-4 w-4 flex-shrink-0 text-zinc-400" />
            <span className="flex-1 truncate text-xs text-zinc-300">
              {selectedFile.name}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedFile(null);
              }}
              className="rounded p-0.5 text-zinc-500 hover:text-zinc-300"
            >
              <FiX className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* Actions */}
        <div className="mt-4 flex items-center gap-2">
          {/* Upload button label */}
          <button
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isUploading ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                Uploading…
              </>
            ) : (
              <>
                <FiUploadCloud className="h-4 w-4" />
                {meta.mode === 'jira'
                  ? 'Upload & Attach to Jira'
                  : 'Upload & Import'}
              </>
            )}
          </button>
          <button
            onClick={() => onDismiss(toolCall.id)}
            disabled={isUploading}
            className="rounded-xl border border-zinc-600 px-4 py-2.5 text-sm font-medium text-zinc-400 transition-colors hover:border-zinc-500 hover:text-zinc-200 disabled:opacity-40"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Renders file upload cards for all pending tool calls that need a file.
 * Mirrors ToolApprovalUI in ChatList — placed right after it.
 */
const ToolFileUploadUI = ({
  fileUploadList,
  onFileUploaded,
  onFileDismissed,
}) => {
  const pendingUploads = fileUploadList.filter(
    (tc) => tc.fileUploadNeeded && !tc.fileUploaded,
  );

  if (!pendingUploads.length) return null;

  return (
    <div className="mx-auto w-full max-w-3xl">
      {pendingUploads.map((toolCall) => (
        <FileUploadCard
          key={toolCall.id}
          toolCall={toolCall}
          onComplete={onFileUploaded}
          onDismiss={onFileDismissed}
        />
      ))}
    </div>
  );
};

export default ToolFileUploadUI;
